import { MaterialCommunityIcons } from "@expo/vector-icons";
import { LinearGradient } from "expo-linear-gradient";
import { useLocalSearchParams, router } from "expo-router";
import { useState, useMemo, useCallback, useRef, useEffect } from "react";
import { ScrollView, StyleSheet, View, Text, Pressable } from "react-native";
import { Snackbar } from "react-native-paper";
import { SafeAreaView } from "react-native-safe-area-context";

import { OrientationCard } from "@/components/assessment/OrientationCard";
import { PhaseCard } from "@/components/assessment/PhaseCard";
import { SubmitButton } from "@/components/assessment/SubmitButton";
import { ClockOutModal, type FinishPayload } from "@/components/visit/ClockOutModal";
import { LiveTranslationButton } from "@/components/visit/LiveTranslationButton";
import { submitAndClockOut, submitForReview, type Section } from "@/lib/api";
import { captureLocation } from "@/lib/capture-location";
import { useFormContext } from "@/lib/FormContext";
import { Recorder } from "@/lib/recorder";
import { colors, fonts, gradients, radius, spacing, shadows } from "@/lib/theme";
import {
    computeVisitState,
    isAssessmentReadOnly,
    isClockedOutPaperworkOpen
} from "@/lib/visit-state";

function computeLockState(
    phases: Array<{ id: string; sections: Section[] }>,
    sectionStatuses: Array<{ sectionId: string | number; status: string }>
) {
    const isSectionVisible = (s: Section) => s.visibleOnMobile !== false;

    let unlockedPhaseIndex = 0;
    for (let i = 0; i < phases.length; i++) {
        const visibleSections = phases[i].sections.filter(isSectionVisible);
        const allComplete = visibleSections.every((sec) => {
            const status = sectionStatuses.find((ss) => String(ss.sectionId) === String(sec.id));
            return status?.status === "completed";
        });
        if (allComplete && visibleSections.length > 0) {
            unlockedPhaseIndex = i + 1;
        } else {
            unlockedPhaseIndex = i;
            break;
        }
    }

    // Within the unlocked phase, find the first incomplete section
    let nextSectionId: string | null = null;
    if (unlockedPhaseIndex < phases.length) {
        const currentPhase = phases[unlockedPhaseIndex];
        const visibleSections = currentPhase.sections.filter(isSectionVisible);
        for (const sec of visibleSections) {
            const status = sectionStatuses.find((ss) => String(ss.sectionId) === String(sec.id));
            if (status?.status !== "completed") {
                nextSectionId = String(sec.id);
                break;
            }
        }
    }

    return { unlockedPhaseIndex, nextSectionId };
}

export default function AssessmentScreen() {
    const { id } = useLocalSearchParams<{ id: string }>();
    const { form, updateForm } = useFormContext();
    const [showSuccess, setShowSuccess] = useState(false);
    const [submitLoading, setSubmitLoading] = useState(false);
    const [clockOutModalVisible, setClockOutModalVisible] = useState(false);
    const [toast, setToast] = useState<string | null>(null);
    const successTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const hasNavigatedRef = useRef(false);

    // Cleanup timer on unmount to prevent double navigation
    useEffect(() => {
        return () => {
            if (successTimerRef.current) {
                clearTimeout(successTimerRef.current);
            }
        };
    }, []);

    const { visit, phases, sectionStatuses } = form;
    const isOpenForResubmission = visit.openForResubmission === true;
    const visitState = computeVisitState(visit);
    const assessmentReadOnly = isAssessmentReadOnly(visitState);

    const { unlockedPhaseIndex, nextSectionId } = useMemo(
        () => computeLockState(phases, sectionStatuses),
        [phases, sectionStatuses]
    );

    const isSectionVisible = (s: Section) => s.visibleOnMobile !== false;
    const allSections = phases.flatMap((p) => p.sections.filter(isSectionVisible));
    const allSectionsComplete = useMemo(() => {
        return (
            allSections.length > 0 &&
            allSections.every((s) => {
                const status = sectionStatuses.find((ss) => String(ss.sectionId) === String(s.id));
                return status?.status === "completed";
            })
        );
    }, [allSections, sectionStatuses]);

    // Completed phases count
    const completedPhases = useMemo(() => {
        return phases.filter((phase) => {
            const visibleSections = phase.sections.filter(isSectionVisible);
            if (visibleSections.length === 0) {
                return false;
            }
            return visibleSections.every((s) => {
                const status = sectionStatuses.find((ss) => String(ss.sectionId) === String(s.id));
                return status?.status === "completed";
            });
        }).length;
    }, [phases, sectionStatuses]);

    const completedSections = allSections.filter((s) => {
        const status = sectionStatuses.find((ss) => String(ss.sectionId) === String(s.id));
        return status?.status === "completed";
    }).length;

    const progress = allSections.length > 0 ? completedSections / allSections.length : 0;

    const handleSectionPress = useCallback(
        (sectionId: string, isLocked: boolean, lockMessage: string, behaviorType?: string) => {
            if (isLocked) {
                setToast(lockMessage);
                return;
            }
            if (assessmentReadOnly) {
                setToast("Clock in to start documenting");
                return;
            }
            // Medications have their own dedicated hub screen
            if (behaviorType === "medications") {
                router.push(`/visit/${id}/medications?sectionId=${sectionId}`);
                return;
            }
            // Teaching is consolidated into a single review screen — no per-section route
            if (behaviorType === "teaching") {
                router.push(`/visit/${id}/teaching`);
                return;
            }
            router.push(`/visit/${id}/section/${sectionId}`);
        },
        [id, assessmentReadOnly]
    );

    const handleSubmit = async () => {
        // Clocked out already (from her next visit's door): submit only, once
        // every section is done. Never the clock-out modal: it would ask her for
        // GPS and a clock-out reason for a clock-out she already has.
        if (isClockedOutPaperworkOpen(form.visit)) {
            if (!allSectionsComplete) {
                setToast(
                    "You already clocked out of this visit. Finish every section, then submit."
                );
                return;
            }
            if (!id) {
                return;
            }
            setSubmitLoading(true);
            try {
                const result = await submitAndClockOut(id);
                updateForm((prev) => ({
                    ...prev,
                    visit: { ...prev.visit, status: result.visit.status }
                }));
                setShowSuccess(true);
                successTimerRef.current = setTimeout(() => {
                    if (!hasNavigatedRef.current) {
                        hasNavigatedRef.current = true;
                        setShowSuccess(false);
                        router.back();
                    }
                }, 2000);
            } catch (e) {
                setToast(e instanceof Error ? e.message : "Submit failed");
            } finally {
                setSubmitLoading(false);
            }
            return;
        }
        if (!allSectionsComplete) {
            if (isOpenForResubmission) {
                setToast("Complete all sections before resubmitting");
                return;
            }
            // She tapped "Submit & clock out" with the assessment unfinished.
            // Don't dead-end with a toast — open the same terminal modal the
            // visit screen uses. With incomplete sections it shows the
            // "Clock out before finishing?" flow (warning + required note) so
            // she can leave early without backing out first.
            setClockOutModalVisible(true);
            return;
        }
        if (!id) {
            return;
        }
        setSubmitLoading(true);
        try {
            if (isOpenForResubmission) {
                const result = await submitForReview(id);
                updateForm((prev) => ({
                    ...prev,
                    visit: {
                        ...prev.visit,
                        status:
                            typeof result.visit === "object" &&
                            result.visit !== null &&
                            "status" in result.visit
                                ? String(result.visit.status)
                                : prev.visit.status,
                        openForResubmission: false
                    }
                }));
                setShowSuccess(true);
                successTimerRef.current = setTimeout(() => {
                    if (!hasNavigatedRef.current) {
                        hasNavigatedRef.current = true;
                        setShowSuccess(false);
                        router.back();
                    }
                }, 2000);
                return;
            }
            // Submitting is the terminal "I'm done here" action — it also clocks
            // her out. Capture GPS the same way the clock-out modal does (the
            // assessment screen has no separate clock-out step). Sections are
            // already complete here, so the server submits + clocks out.
            const cap = await captureLocation();
            if (!cap.location) {
                // No GPS on the direct submit path → hand off to the terminal
                // modal, which owns the retry / open-settings / typed-reason
                // flow (the server now rejects a no-GPS clock-out without a
                // reason, so we must not submit blind here).
                setSubmitLoading(false);
                setClockOutModalVisible(true);
                return;
            }
            const result = await submitAndClockOut(id, cap.location);
            updateForm((prev) => ({
                ...prev,
                visit: {
                    ...prev.visit,
                    status: result.visit.status,
                    clockOutAt: result.visit.clockOutAt,
                    clockOutLocation: result.visit.clockOutLocation ?? null
                }
            }));
            // Visit Recording — close the active session and enqueue finalize.
            // Best-effort: never block the submit flow on recorder errors.
            try {
                await Recorder.stop("submit");
            } catch (err) {
                console.warn("[recorder] stop on submit failed", err);
            }
            setShowSuccess(true);
            successTimerRef.current = setTimeout(() => {
                if (!hasNavigatedRef.current) {
                    hasNavigatedRef.current = true;
                    setShowSuccess(false);
                    router.back();
                }
            }, 2000);
        } catch (e) {
            setToast(e instanceof Error ? e.message : "Submit failed");
        } finally {
            setSubmitLoading(false);
        }
    };

    // Terminal clock-out from the modal opened on an INCOMPLETE assessment.
    // The modal requires a note in that case; the server clocks out without
    // submitting (status stays in_progress — the tracked exception). Mirrors
    // the visit screen's handleFinish.
    const handleClockOutFromModal = async (payload?: FinishPayload) => {
        if (!id) {
            return;
        }
        setSubmitLoading(true);
        try {
            const result = await submitAndClockOut(id, payload);
            updateForm((prev) => ({
                ...prev,
                visit: {
                    ...prev.visit,
                    status: result.visit.status,
                    clockOutAt: result.visit.clockOutAt,
                    clockOutLocation: result.visit.clockOutLocation ?? null
                }
            }));
            try {
                await Recorder.stop("submit");
            } catch (err) {
                console.warn("[recorder] stop on clock-out failed", err);
            }
            setClockOutModalVisible(false);
            // Back to the visit screen, which now reads as terminal.
            if (router.canGoBack()) {
                router.back();
            } else {
                router.replace(`/visit/${id}`);
            }
        } catch (e) {
            setToast(e instanceof Error ? e.message : "Clock out failed");
            setClockOutModalVisible(false);
        } finally {
            setSubmitLoading(false);
        }
    };

    const isPhaseCompleted = useCallback(
        (phase: (typeof phases)[number]) => {
            const visibleSections = phase.sections.filter(isSectionVisible);
            return (
                visibleSections.length > 0 &&
                visibleSections.every((s) => {
                    const status = sectionStatuses.find(
                        (ss) => String(ss.sectionId) === String(s.id)
                    );
                    return status?.status === "completed";
                })
            );
        },
        [sectionStatuses]
    );

    return (
        <SafeAreaView style={styles.container} edges={["top"]}>
            {/* Header */}
            <View style={styles.header}>
                <Pressable
                    onPress={() => {
                        if (router.canGoBack()) {
                            router.back();
                        } else {
                            router.replace("/(tabs)/visits");
                        }
                    }}
                    style={styles.backRow}
                >
                    <MaterialCommunityIcons name="chevron-left" size={22} color={colors.teal500} />
                    <Text style={styles.backText}>Back</Text>
                </Pressable>
                <Text style={styles.headerTitle}>Assessment</Text>
                <Text style={styles.headerCount}>
                    {completedPhases}/{phases.length} phases
                </Text>
            </View>

            {/* Overall progress bar */}
            <View style={styles.progressContainer}>
                <View style={styles.progressTrack}>
                    <LinearGradient
                        colors={gradients.brand}
                        start={{ x: 0, y: 0 }}
                        end={{ x: 1, y: 0 }}
                        style={[styles.progressFill, { width: `${Math.round(progress * 100)}%` }]}
                    />
                </View>
            </View>

            {/* Live Translation entry — only while clocked in */}
            {form.visit?.id && form.visit.status === "in_progress" ? (
                <View style={styles.translationContainer}>
                    <LiveTranslationButton
                        visitId={form.visit.id}
                        patientLanguage={form.patient?.language ?? null}
                    />
                </View>
            ) : null}

            {/* Read-only banner */}
            {visitState === 1 ? (
                <View style={styles.bannerWarning}>
                    <Text style={styles.bannerText}>Preview — clock in to start documenting</Text>
                </View>
            ) : null}
            {visitState === 4 ? (
                <View style={styles.bannerInfo}>
                    <Text style={styles.bannerText}>Visit completed — view only</Text>
                </View>
            ) : null}

            {/* Phase cards */}
            <ScrollView
                contentContainerStyle={styles.scrollContent}
                showsVerticalScrollIndicator={false}
            >
                {/* Special / Ad-Hoc visits: read-only RN briefing at the top (P5). */}
                {form.formSource === "special" && form.orientation ? (
                    <OrientationCard orientation={form.orientation} />
                ) : null}
                {phases.map((phase, i) => {
                    const isWelcomePackage = phase.name === "Welcome Package";
                    return (
                        <PhaseCard
                            key={phase.id}
                            phase={phase}
                            phaseIndex={i}
                            isLocked={i > unlockedPhaseIndex}
                            isCompleted={isPhaseCompleted(phase)}
                            sectionStatuses={sectionStatuses}
                            nextSectionId={nextSectionId}
                            onSectionPress={handleSectionPress}
                            readOnly={assessmentReadOnly}
                            collapsed={isWelcomePackage}
                            onCollapsedPress={
                                isWelcomePackage
                                    ? () => router.push(`/visit/${id}/welcome-package`)
                                    : undefined
                            }
                        />
                    );
                })}
            </ScrollView>

            {/* Submit button */}
            {!assessmentReadOnly ? (
                <SubmitButton
                    allComplete={allSectionsComplete}
                    onPress={handleSubmit}
                    loading={submitLoading}
                    label={isOpenForResubmission ? "RESUBMIT" : undefined}
                />
            ) : null}

            {/* Clock-out modal — opened when the nurse taps Submit & clock out
                with the assessment still incomplete (leave-early exception). No
                "Return to Assessment" action since she's already here. */}
            <ClockOutModal
                visible={clockOutModalVisible}
                visit={visit}
                patient={form.patient}
                completedSections={completedSections}
                totalSections={allSections.length}
                onConfirm={handleClockOutFromModal}
                onCancel={() => setClockOutModalVisible(false)}
            />

            {/* Success overlay */}
            {showSuccess ? (
                <View style={styles.successOverlay}>
                    <View style={styles.successCard}>
                        <View style={styles.successCheck}>
                            <MaterialCommunityIcons name="check" size={32} color="#FFFFFF" />
                        </View>
                        <Text style={styles.successTitle}>
                            {isOpenForResubmission ? "Resubmitted" : "Submitted & clocked out"}
                        </Text>
                    </View>
                </View>
            ) : null}

            {/* Toast */}
            <Snackbar
                visible={!!toast}
                onDismiss={() => setToast(null)}
                duration={3000}
                style={styles.snackbar}
            >
                {toast}
            </Snackbar>
        </SafeAreaView>
    );
}

const styles = StyleSheet.create({
    container: {
        flex: 1,
        backgroundColor: colors.background
    },
    header: {
        paddingHorizontal: spacing.base,
        paddingTop: spacing.sm,
        paddingBottom: spacing.md,
        backgroundColor: colors.surface,
        borderBottomWidth: 1,
        borderBottomColor: colors.border
    },
    backRow: {
        flexDirection: "row",
        alignItems: "center",
        marginLeft: -spacing.xs
    },
    backText: {
        fontFamily: fonts.semiBold,
        fontSize: 14,
        color: colors.teal500
    },
    headerTitle: {
        fontFamily: fonts.bold,
        fontSize: 18,
        color: colors.textPrimary,
        marginTop: spacing.xs
    },
    headerCount: {
        fontFamily: fonts.medium,
        fontSize: 12,
        color: colors.textSecondary
    },
    progressContainer: {
        paddingHorizontal: spacing.base,
        paddingVertical: spacing.sm,
        backgroundColor: colors.surface
    },
    translationContainer: {
        paddingHorizontal: spacing.base,
        paddingBottom: spacing.sm,
        backgroundColor: colors.surface
    },
    progressTrack: {
        height: 6,
        backgroundColor: colors.gray200,
        borderRadius: 3,
        overflow: "hidden"
    },
    progressFill: {
        height: 6,
        borderRadius: 3
    },
    bannerWarning: {
        backgroundColor: colors.warning50,
        paddingVertical: spacing.sm,
        paddingHorizontal: spacing.base,
        borderBottomWidth: 1,
        borderBottomColor: colors.warning400
    },
    bannerInfo: {
        backgroundColor: colors.info50,
        paddingVertical: spacing.sm,
        paddingHorizontal: spacing.base,
        borderBottomWidth: 1,
        borderBottomColor: colors.info400
    },
    bannerText: {
        fontFamily: fonts.semiBold,
        fontSize: 13,
        color: colors.textSecondary,
        textAlign: "center"
    },
    scrollContent: {
        paddingBottom: 100
    },
    successOverlay: {
        ...StyleSheet.absoluteFill,
        backgroundColor: "rgba(31, 21, 71, 0.5)",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 100
    },
    successCard: {
        backgroundColor: colors.surface,
        borderRadius: radius["2xl"],
        padding: spacing["2xl"],
        alignItems: "center",
        ...shadows.xl
    },
    successCheck: {
        width: 64,
        height: 64,
        borderRadius: 32,
        backgroundColor: colors.success500,
        alignItems: "center",
        justifyContent: "center",
        marginBottom: spacing.base
    },
    successCheckText: {
        color: "#FFFFFF",
        fontSize: 28,
        fontWeight: "700"
    },
    successTitle: {
        fontFamily: fonts.semiBold,
        fontSize: 16,
        color: colors.textPrimary,
        textAlign: "center"
    },
    snackbar: {
        backgroundColor: colors.warning500
    }
});
